import React from 'react';
import { TrackCurveHeader } from './logTracks';
import styles from './TrackHeader.module.css';

export interface TrackHeaderProps {
  trackKey: string;
  title: string;
  kind: 'depth' | 'curve' | 'category';
  curves: TrackCurveHeader[];
}

const formatBound = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return '—';
  if (Number.isInteger(value)) return String(value);
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 1) return value.toFixed(2);
  return value.toPrecision(2);
};

/**
 * The header for one track: a pill per curve, carrying its colour, name, unit
 * and the min/max it is scaled to.
 *
 * Modelled on the reference log software's track headers (well-gold's
 * `ChartsHeader`), with two deliberate departures for this app's light
 * surfaces: the colour is a 3px accent stripe and a dot rather than a filled
 * pill -- four saturated fills stacked on the Sw track would read as a colour
 * block, and white-on-amber fails contrast -- and the scale hint is a native
 * `title` rather than a portalled tooltip, since there is no popover here to
 * fight with.
 *
 * The scale printed here is the *same object* handed to the chart's value axis,
 * so the two cannot disagree. That is why the axes are pinned rather than
 * auto-scaled: a header that restated itself on every zoom would be worse than
 * no header at all.
 */
export const TrackHeader: React.FC<TrackHeaderProps> = ({
  trackKey,
  title,
  kind,
  curves
}) => {
  // Don't say it twice: a single curve named after its track needs the pill
  // only. Multi-curve tracks keep the caption, which is what groups them.
  const showTitle =
    curves.length !== 1 || curves[0].label !== title || kind !== 'curve';

  return (
    <div className={styles.header} data-testid={`track-header-${trackKey}`}>
      {showTitle && (
        <span className={styles.title} title={title}>
          {title}
        </span>
      )}
      {curves.length > 0 && (
        <div
          className={styles.pills}
          // Two columns past five pills, so a busy track's header cannot
          // outgrow the plots it sits over. Nothing reaches this today; the
          // hidden GR/CALI/RHOB/PHIE tracks would.
          data-columns={curves.length > 5 ? 2 : 1}
        >
          {curves.map(curve => (
            <div
              key={curve.id}
              className={styles.pill}
              style={
                { '--curve-color': curve.color } as React.CSSProperties
              }
              title={`Scale: ${formatBound(curve.min)} – ${formatBound(
                curve.max
              )}`}
              data-testid={`curve-pill-${curve.id}`}
            >
              {/*
                * Scale bounds at the two edges with the name centred between
                * them -- the wireline convention, which reads as "this track
                * runs from here to here" rather than as a label with two
                * numbers stuck on the end.
                */}
              <span
                className={styles.bound}
                data-testid={`curve-min-${curve.id}`}
              >
                {formatBound(curve.min)}
              </span>
              <span className={styles.label}>
                <span
                  className={
                    curve.shape === 'points'
                      ? styles.swatchPoint
                      : styles.swatch
                  }
                />
                {curve.label}
                {curve.unit && (
                  <span className={styles.unit}>{curve.unit}</span>
                )}
              </span>
              <span
                className={styles.bound}
                data-testid={`curve-max-${curve.id}`}
              >
                {formatBound(curve.max)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(TrackHeader);
